/**
 * ActivityView — Phase E.4 unified 24/7 activity timeline.
 *
 * Aggregates 4 event sources (audit / prospector ticks / email sends /
 * SendGrid events) into a single timeline so the owner can scan "what
 * happened" at a glance.
 *
 * Filters: time window, type subset, free-text search.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Clock,
  Crosshair,
  Loader2,
  Mail,
  RefreshCw,
  Search,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { crmApi } from "@/lib/ownerClient";

const HOURS_OPTS = [
  { value: "1", label: "Last 1 hour" },
  { value: "6", label: "Last 6 hours" },
  { value: "24", label: "Last 24 hours" },
  { value: "72", label: "Last 3 days" },
  { value: "168", label: "Last 7 days" },
];

const TYPES = [
  { value: "audit", label: "Audit", icon: ShieldCheck, color: "border-emerald-500/40 text-emerald-200" },
  { value: "tick", label: "Prospector tick", icon: Crosshair, color: "border-amber-500/40 text-amber-200" },
  { value: "email_send", label: "Email send", icon: Mail, color: "border-violet-500/40 text-violet-200" },
  { value: "email_event", label: "Email event", icon: Activity, color: "border-blue-500/40 text-blue-200" },
];

const TYPE_META = Object.fromEntries(TYPES.map((t) => [t.value, t]));

function OutcomeIcon({ outcome }) {
  if (outcome === "pass") return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" />;
  if (outcome === "fail") return <XCircle className="h-3.5 w-3.5 text-red-300" />;
  if (outcome === "warn") return <AlertCircle className="h-3.5 w-3.5 text-amber-300" />;
  return <Clock className="h-3.5 w-3.5 text-zinc-400" />;
}

function relTime(ts) {
  if (!ts) return "";
  const dt = new Date(ts);
  const now = new Date();
  const sec = Math.round((now - dt) / 1000);
  if (sec < 0) return "in the future";
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return dt.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function TimelineItem({ item }) {
  const meta = TYPE_META[item.type] || TYPES[0];
  const Icon = meta.icon;
  return (
    <div
      className="flex items-start gap-3 px-3 py-2.5 border-b border-zinc-900 hover:bg-zinc-900/30 transition-colors"
      data-testid={`activity-item-${item.type}`}
    >
      <div className="mt-0.5">
        <div className={`rounded-md p-1 border ${meta.color} bg-zinc-950`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <OutcomeIcon outcome={item.outcome} />
            <div className="text-[13px] text-white truncate" title={item.title}>
              {item.title}
            </div>
          </div>
          <div className="text-[10.5px] text-zinc-500 shrink-0" title={item.ts}>
            {relTime(item.ts)}
          </div>
        </div>
        <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-2">
          <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[9.5px] py-0">
            {item.actor}
          </Badge>
          {Array.isArray(item.tags) && item.tags.length > 0
            ? item.tags.slice(0, 3).map((t) => (
                <span key={t} className="text-[10px] text-zinc-600 uppercase tracking-[0.12em]">
                  {t}
                </span>
              ))
            : null}
        </div>
        {/* Compact detail summary */}
        <DetailSummary type={item.type} detail={item.detail || {}} />
      </div>
    </div>
  );
}

function DetailSummary({ type, detail }) {
  if (!detail || Object.keys(detail).length === 0) return null;
  if (type === "tick") {
    const bySource = detail.by_source || {};
    const keys = Object.keys(bySource);
    if (keys.length === 0) return null;
    return (
      <div className="text-[11px] text-zinc-500 mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
        {keys.map((k) => {
          const s = bySource[k] || {};
          return (
            <span key={k} className="flex items-center gap-1">
              <span className="text-zinc-400">{k}:</span>
              <span>
                +{s.inserted || 0}
                {s.skipped_dupes ? <span className="text-zinc-600"> · dupe {s.skipped_dupes}</span> : null}
                {s.error ? <span className="text-red-300"> · {s.error.slice(0, 40)}</span> : null}
              </span>
            </span>
          );
        })}
      </div>
    );
  }
  if (type === "email_send") {
    const recipient = detail.recipient_email || "";
    const seq = detail.sequence_slug || detail.template_slug || "";
    return (
      <div className="text-[11px] text-zinc-500 mt-1.5">
        {recipient}
        {seq ? <span> · seq {seq}</span> : null}
        {detail.open_count > 0 ? <span className="text-emerald-300"> · opened {detail.open_count}×</span> : null}
        {detail.click_count > 0 ? <span className="text-amber-300"> · clicked {detail.click_count}×</span> : null}
        {detail.bounce_reason ? <span className="text-red-300"> · {detail.bounce_reason.slice(0, 50)}</span> : null}
      </div>
    );
  }
  if (type === "email_event") {
    return (
      <div className="text-[11px] text-zinc-500 mt-1.5">
        {detail.recipient_email}
        {detail.url ? <span className="text-amber-300 ml-2">→ {detail.url.slice(0, 60)}</span> : null}
        {detail.reason ? <span className="text-red-300 ml-2">· {detail.reason.slice(0, 80)}</span> : null}
      </div>
    );
  }
  // audit
  const summaryKeys = ["prospect_id", "sequence_slug", "audience", "recipient_email", "label", "requested"];
  const bits = summaryKeys
    .map((k) => (detail[k] ? `${k}=${typeof detail[k] === "string" ? detail[k] : JSON.stringify(detail[k])}` : null))
    .filter(Boolean);
  if (bits.length === 0) return null;
  return (
    <div className="text-[11px] text-zinc-500 mt-1.5 truncate">
      {bits.slice(0, 4).join(" · ")}
    </div>
  );
}

export default function ActivityView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [hours, setHours] = useState("24");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [activeTypes, setActiveTypes] = useState(() => new Set(TYPES.map((t) => t.value)));

  const typesParam = useMemo(() => {
    if (activeTypes.size === 0 || activeTypes.size === TYPES.length) return undefined;
    return Array.from(activeTypes).join(",");
  }, [activeTypes]);

  const reload = useCallback(
    async (refreshOnly = false) => {
      if (refreshOnly) setRefreshing(true);
      else setLoading(true);
      try {
        const d = await crmApi.activityFeed({
          hours,
          limit: 200,
          types: typesParam,
          q: search || undefined,
        });
        setData(d);
      } catch (err) {
        toast.error(err.message || "Failed to load activity");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [hours, typesParam, search],
  );

  useEffect(() => {
    reload();
  }, [reload]);

  // Auto-refresh every 20s if enabled
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => reload(true), 20000);
    return () => clearInterval(id);
  }, [autoRefresh, reload]);

  function toggleType(t) {
    setActiveTypes((cur) => {
      const next = new Set(cur);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  }

  function onSearchSubmit(e) {
    e.preventDefault();
    setSearch(searchInput.trim());
  }

  const items = data?.items || [];
  const totals = data?.totals || {};

  return (
    <div className="space-y-6" data-testid="owner-activity-view">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-white">
            <Activity className="h-5 w-5 text-amber-400" />
            24/7 Activity
          </h1>
          <p className="text-sm text-zinc-400">
            Unified timeline of every CRM event — audits, prospector ticks, email sends, and SendGrid webhook events.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={hours} onValueChange={setHours}>
            <SelectTrigger className="h-9 w-[150px] bg-zinc-950 border-zinc-800 text-white" data-testid="activity-window-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HOURS_OPTS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant={autoRefresh ? "default" : "outline"}
            onClick={() => setAutoRefresh((v) => !v)}
            className={autoRefresh ? "bg-amber-500 hover:bg-amber-400 text-black font-semibold" : "border-zinc-800 text-white hover:bg-zinc-900"}
            data-testid="activity-autorefresh-toggle"
          >
            {autoRefresh ? "Live" : "Paused"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => reload(true)}
            disabled={refreshing}
            className="border-zinc-800 text-white hover:bg-zinc-900"
            data-testid="activity-refresh"
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Filter row */}
      <Card className="bg-zinc-950 border-zinc-800">
        <CardContent className="p-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mr-1">
              Types
            </span>
            {TYPES.map((t) => {
              const on = activeTypes.has(t.value);
              const Icon = t.icon;
              const n = totals[t.value] ?? 0;
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => toggleType(t.value)}
                  className={`px-2.5 py-1 rounded text-[11px] border transition-colors flex items-center gap-1.5 ${
                    on
                      ? `${t.color} bg-zinc-950`
                      : "border-zinc-800 text-zinc-600 hover:text-zinc-400"
                  }`}
                  data-testid={`activity-toggle-${t.value}`}
                >
                  <Icon className="h-3 w-3" />
                  {t.label}
                  <span className="text-[10px] opacity-70">({n})</span>
                </button>
              );
            })}
          </div>
          <form onSubmit={onSearchSubmit} className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1 block">
                Search
              </Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="title, actor, email…"
                  className="h-9 pl-8 bg-black border-zinc-800 text-white placeholder:text-zinc-600"
                  data-testid="activity-search-input"
                />
              </div>
            </div>
            <Button type="submit" className="h-9 bg-amber-500 hover:bg-amber-400 text-black">
              Search
            </Button>
            {search ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setSearchInput("");
                  setSearch("");
                }}
                className="h-9 text-zinc-400 hover:text-white"
              >
                Clear
              </Button>
            ) : null}
          </form>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card className="bg-zinc-950 border-zinc-800">
        <CardContent className="p-0">
          {loading && (
            <div className="flex items-center justify-center py-12 text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading activity…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="py-12 text-center" data-testid="activity-empty">
              <Activity className="h-6 w-6 text-amber-400 mx-auto mb-2" />
              <div className="text-white text-sm font-medium">No events in this window</div>
              <div className="text-zinc-500 text-[12px] mt-1">
                Try widening the time window or removing filters.
              </div>
            </div>
          )}
          {!loading && items.length > 0 && (
            <div className="max-h-[640px] overflow-y-auto" data-testid="activity-feed-list">
              {items.map((it, idx) => (
                <TimelineItem key={`${it.type}-${it.ts}-${idx}`} item={it} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-[11px] text-zinc-600 text-right">
        Showing {items.length} of {data?.count || 0} · auto-refresh {autoRefresh ? "on" : "off"}
      </div>
    </div>
  );
}
